import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowUpRight, Phone } from 'lucide-react';
import BookACallSheet from './BookACallSheet';

/**
 * MobileStickyCta — bottom conversion bar that fades in after the user
 * scrolls past the hero on mobile. Two-tap booking flow:
 *   Tap 1: opens BookACallSheet
 *   Tap 2: visitor confirms name + phone → routes to /api/leads/book-a-call
 *
 * Hidden on:
 *  - Desktop (>=lg)
 *  - When the /contact section is on screen (avoids stacked CTAs)
 *  - Sports Management + Matchmaker pages (they have dedicated flows)
 */
export default function MobileStickyCta() {
  const [visible, setVisible] = useState(false);
  const [nearContact, setNearContact] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const { pathname, hash } = useLocation();

  const suppressed = pathname.startsWith('/sports-management') ||
                     pathname.startsWith('/matchmaker') ||
                     pathname.startsWith('/ralli') ||
                     hash === '#contact';

  useEffect(() => {
    if (suppressed) { setVisible(false); return; }
    const onScroll = () => {
      const y = window.scrollY;
      const vh = window.innerHeight;
      setVisible(y > vh * 0.6);
      const contact = document.getElementById('contact');
      if (contact) {
        const rect = contact.getBoundingClientRect();
        setNearContact(rect.top < vh * 0.8);
      } else {
        setNearContact(false);
      }
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [suppressed, pathname]);

  if (suppressed) return null;

  const isVisible = visible && !nearContact && !sheetOpen;

  return (
    <>
      <div
        aria-hidden={!isVisible}
        className={`lg:hidden fixed left-0 right-0 z-[280] px-4 transition-all duration-500 ease-out ${
          isVisible ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-6 pointer-events-none'
        }`}
        style={{ bottom: 'max(16px, env(safe-area-inset-bottom))' }}
        data-testid="mobile-sticky-cta"
      >
        <div className="relative flex items-stretch gap-2 rounded-full bg-black/85 border border-white/12 backdrop-blur-xl shadow-[0_20px_60px_-15px_rgba(255,61,46,0.35)] p-1.5">
          {/* Live pulse */}
          <span className="absolute -top-1 -left-1 w-3 h-3 pointer-events-none">
            <span className="absolute inset-0 rounded-full bg-[#FF3D2E] animate-ping opacity-70" />
            <span className="absolute inset-[3px] rounded-full bg-[#FF3D2E]" />
          </span>

          <a
            href="tel:+16195524677"
            className="shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/[0.04] border border-white/10 text-white/85 active:bg-white/10 transition-colors"
            aria-label="Call SMG directly"
            data-testid="mobile-sticky-call"
          >
            <Phone className="w-4 h-4" />
          </a>

          <button
            type="button"
            onClick={() => setSheetOpen(true)}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#FF3D2E] text-white text-[11px] tracking-[0.28em] font-medium px-5 py-3 active:bg-[#ff5446] transition-colors"
            data-testid="mobile-sticky-book"
          >
            BOOK A CALL
            <ArrowUpRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <BookACallSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        source={`mobile-sticky-cta:${pathname}`}
      />
    </>
  );
}
